import React, { useState, useEffect } from "react";
import axios from "axios";
import "./SupportCase.css";
import { useNavigate } from "react-router-dom";
import Nav from "../Navbar-footer/Nav";

const plans = {
  monthly: [
    
    {
      title: "Silver",
      price: "₹200",
      amount: 200,
      duration: "/month",
      description: "Essential features for occasional donors",
      features: [
        "Access to blood donation alerts",
        "Basic donor profile",
        "Digital donor badge",
        "Email notifications",
      ],
    },
    {
      title: "Gold",
      price: "₹600",
      amount: 600,
      duration: "/month",
      description: "Enhanced features for regular donors",
      features: [
        "Priority notifications for donations",
        "Exclusive blood drive invitations",
        "Donor achievement badges",
        "Monthly impact reports",
        "24/7 donor support",
      ],
    },
    {
      title: "Platinum",
      price: "₹1100",
      amount: 1100,
      duration: "/month",
      description: "Premium service for dedicated donors",
      features: [
        "Personal donor support assistant",
        "Top-priority blood alerts",
        "VIP access to donation events",
        "Personalized health insights",
        "Exclusive donor community access",
      ],
    },
  ],
  yearly: [
    {
      title: "Silver",
      price: "₹2400",
      amount: 2400,
      duration: "/year",
      description: "Essential features for occasional donors",
      features: [
        "Access to blood donation alerts",
        "Basic donor profile",
        "Digital donor badge",
        "Email notifications",
      ],
    },
    {
      title: "Gold",
      price: "₹7200",
      amount: 7200,
      duration: "/year",
      description: "Enhanced features for regular donors",
      features: [
        "Priority notifications for donations",
        "Exclusive blood drive invitations",
        "Donor achievement badges",
        "Monthly impact reports",
        "24/7 donor support",
      ],
    },
    {
      title: "Platinum",
      price: "₹13200",
      amount: 13200,
      duration: "/year",
      description: "Premium service for dedicated donors",
      features: [
        "Personal donor support assistant",
        "Top-priority blood alerts",
        "VIP access to donation events",
        "Personalized health insights",
        "Exclusive donor community access",
      ],
    },
  ],
};



// Decode JWT token
function decodeToken(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
  } catch (e) {
    return null;
  }
}

function MembershipPlans() {
  const [planType, setPlanType] = useState("monthly");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeMembership, setActiveMembership] = useState(null);
  const navigate = useNavigate();

  const token = sessionStorage.getItem("token");
  const user = token ? decodeToken(token) : null;

  // Fetch active membership on component load
  useEffect(() => {
    if (!token || !user) return;
    const fetchMembership = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/membership/get-membership`,
          config
        );
        setActiveMembership(res.data.status === "active" ? res.data : null);
      } catch {
        setActiveMembership(null);
      }
    };
    fetchMembership();
  }, [token, user]);

  const handleMembershipClick = async (plan) => {
    if (!token || !user) {
      navigate("/login");
      return;
    }

    if (
      activeMembership &&
      activeMembership.plan === plan.title &&
      activeMembership.duration === planType
    ) {
      setMessage(
        `You already have an active membership (${activeMembership.plan} - ${activeMembership.duration}).`
      );
      return;
    }

    try {
      setLoading(true);
      setMessage("");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const subscriptionResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/membership/create-subscription`,
        { plan: plan.title, duration: planType },
        config
      );

      const { subscriptionId, key } = subscriptionResponse.data;

      const options = {
        key,
        subscription_id: subscriptionId,
        currency: "INR",
        name: "Sarv Sahayata Sangathan Membership",
        description: `${plan.title} Membership`,
        prefill: { name: user.name, email: user.email },
        theme: { color: "#dc3545" },
        handler: async function (response) {
          try {
            const verifyRes = await axios.post(
              `${process.env.REACT_APP_API_URL}/api/membership/verify-payment`,
              {
                subscriptionId,
                plan: plan.title,
                duration: planType,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              config
            );
            setMessage(
              "Membership activated! A confirmation email has been sent."
            );
            setActiveMembership(verifyRes.data.membership);
          } catch (err) {
            console.error(err);
            setMessage(
              "Payment verified but failed to activate membership. Contact support."
            );
          }
        },
        method: {
          card: true,
          netbanking: true,
          upi: true,
          wallet: true,
        },
        modal: {
          ondismiss: function () {
            // setMessage("Payment popup closed.");
          },
        },
      };

      const razor = new window.Razorpay(options);
      razor.open();
    } catch (error) {
      console.error(error);
      setMessage(
        error.response?.data?.error ||
          "Something went wrong while processing payment."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelMembership = async () => {
    if (!token || !user) {
      navigate("/login");
      return;
    }
    try {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/membership/cancel-subscription`,
        {},
        config
      );
      setMessage("Membership cancelled successfully.");
      setActiveMembership(null);
    } catch (error) {
      console.error(error);
      setMessage(error.response?.data?.error || "Failed to cancel membership.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Nav />
    <div className="membership-section">
      <div className="overlay">
        <h1 className="section-title1">Membership Plans</h1>
        <p className="section-desc">
          Join our membership program to enhance your blood donation experience
          and help save more lives.
        </p>

        <div className="toggle-wrapper">
          <span className={planType === "monthly" ? "active" : ""}>
            Monthly
          </span>
          <label className="switch">
            <input
              type="checkbox"
              onChange={() =>
                setPlanType(planType === "monthly" ? "yearly" : "monthly")
              }
            />
            <span className="slider1 round"></span>
          </label>
          <span className={planType === "yearly" ? "active" : ""}>Yearly</span>
        </div>

        <div className="plans-container">
          {plans[planType].map((plan, index) => {
            const isSubscribed =
              activeMembership &&
              activeMembership.plan === plan.title &&
              activeMembership.duration === planType;

            return (
              <div
                className={`plan-box ${index === 1 ? "highlight" : ""}`}
                key={index}
              >
                <h2>{plan.title}</h2>
                <p className="desc">{plan.description}</p>
                <div className="price">
                  <span>{plan.price}</span>
                  <small>{plan.duration}</small>
                </div>
                <ul>
                  {plan.features.map((f, i) => (
                    <li key={i}>✔ {f}</li>
                  ))}
                </ul>
                <button
                  className="join-btn"
                  onClick={() => handleMembershipClick(plan)}
                  disabled={loading || isSubscribed}
                >
                  {isSubscribed
                    ? "Already Subscribed"
                    : loading
                    ? "Processing..."
                    : "Get Membership"}
                </button>

                {isSubscribed && (
                  <button
                    className="cancel-btn"
                    onClick={handleCancelMembership}
                    disabled={loading}
                    style={{
                      marginTop: "10px",
                      backgroundColor: "#dc3545",
                      padding: "12px 18px",
                      color: "#FFF",
                      fontSize: "1rem",
                      cursor: "pointer",
                      borderRadius: "10px",
                      transition: "background 0.3s ease",
                    }}
                  >
                    {loading ? "Processing..." : "Cancel Membership"}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {message && <p className="status-message">{message}</p>}
      </div>
    </div>
    </>
  );
}

export default MembershipPlans;
